import type { Lang } from '@platform/core/types';
import type { IModuleManifest } from '@platform/core/types/modularity/IModuleManifest';
import { registerView } from '@platform/engine/registry/viewRegistry';
import { registerModuleDefinition } from './moduleDefinitionRegistry';
import { i18n } from '@platform/i18n';
import { moduleNamespace } from '@platform/i18n/moduleBundle';

const loaded = new Map<string, Promise<void>>();
const settled = new Set<string>();

const cacheKeyOf = (manifest: IModuleManifest, lang: Lang): string => `${manifest.key}|${lang}`;

/**
 * Ліниво завантажує пакет модуля: зріз стану (injectInto + реєстрація у moduleSliceRegistry), рядки i18n (namespace `module.<ключ>`),
 * власні представлення (viewRegistry) і визначення сторінок пакета (moduleDefinitionRegistry). Повторні виклики повертають той самий проміс; невдале завантаження вилучається з кешу,
 * тож наступний виклик повторює спробу замість повернення кешованої відмови.
 */
export function loadModule(manifest: IModuleManifest, lang: Lang): Promise<void> {
  const cacheKey = cacheKeyOf(manifest, lang);
  let pending = loaded.get(cacheKey);
  if (!pending) {
    pending = Promise.all([
      manifest.loadState(),
      manifest.loadI18n(lang).then((strings) => i18n.addResourceBundle(lang, moduleNamespace(manifest.key), strings, true, true)),
      manifest.loadViews?.().then((views) => {
        for (const [key, component] of Object.entries(views)) registerView(key, component);
      }),
      manifest.loadDefinition?.().then((definition) => registerModuleDefinition(manifest.key, definition)),
    ]).then(() => {
      settled.add(cacheKey);
    });
    pending.catch(() => loaded.delete(cacheKey));
    loaded.set(cacheKey, pending);
  }
  return pending;
}

/** Чи пакет уже завантажено для мови: дає рендер без кадру Spinner при повторному відкритті модуля. */
export function isModuleLoaded(manifest: IModuleManifest, lang: Lang): boolean {
  return settled.has(cacheKeyOf(manifest, lang));
}
